import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus, Pencil, History } from 'lucide-react';
import dayjs from 'dayjs';
import {
  CreateQuranStudentTargetPayload,
  QuranStudentTargetRow,
  createQuranTrackerApi,
  QuranTrackerScope,
  UpdateQuranStudentTargetPayload,
} from '@/services/quran-tracker.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { FeesApi } from '@/services/fees.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { useTerminology } from '@/hooks/useTerminology';

const columnHelper = createColumnHelper<QuranStudentTargetRow>();

const STATUS_TONE: Record<string, 'amber' | 'green' | 'blue'> = {
  Assigned: 'amber',
  'In Progress': 'blue',
  Completed: 'green',
  Reviewed: 'green',
};

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/** QT Students Target — assign targets to students, track progress, view evaluation history. `scope="organization"` renders this natively inside the Org Console, hitting the org's own operating school. */
export function QuranStudentTargetsPage({ scope = 'school' }: { scope?: QuranTrackerScope }) {
  const term = useTerminology();
  const qc = useQueryClient();
  const isOrg = scope === 'organization';
  const quranApi = useMemo(() => createQuranTrackerApi(scope), [scope]);
  const dt = useDataTable({ defaultLimit: 20 });

  const [classFilter, setClassFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assignOpen, setAssignOpen] = useState(false);
  const [progressTarget, setProgressTarget] = useState<QuranStudentTargetRow | null>(null);
  const [historyTarget, setHistoryTarget] = useState<QuranStudentTargetRow | null>(null);

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classFilter, statusFilter]);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', scope, 'for-quran-student-targets'],
    queryFn: () => (isOrg ? quranApi.classesPicker() : AcademicClassesApi.list({ limit: 100 }).then((r) => r.items)),
  });
  const classes = classesPage ?? [];

  const listParams = {
    ...dt.params,
    classId: classFilter || undefined,
    status: statusFilter || undefined,
  };
  const { data, isFetching } = useQuery({
    queryKey: ['quran-student-targets', scope, listParams],
    queryFn: () => quranApi.listStudentTargets(listParams),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['quran-student-targets', scope] });

  const columns = useMemo(
    () => [
      columnHelper.accessor('studentName', { header: 'Student' }),
      columnHelper.accessor('admissionNo', { header: 'Admission #' }),
      columnHelper.accessor('className', { header: term.level }),
      columnHelper.accessor('surahName', { header: 'Surah' }),
      columnHelper.display({
        id: 'ayahRange',
        header: 'Ayah Range',
        cell: (ctx) => `${ctx.row.original.ayathFrom} – ${ctx.row.original.ayathTo}`,
      }),
      columnHelper.display({
        id: 'teacher',
        header: 'Assigned Teacher',
        cell: (ctx) => ctx.row.original.assignedTeacherName ?? '—',
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (ctx) => <Badge tone={STATUS_TONE[ctx.getValue()] ?? 'amber'}>{ctx.getValue()}</Badge>,
      }),
      columnHelper.accessor('completedPercent', {
        header: 'Progress',
        cell: (ctx) => (
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-indigo-500" style={{ width: `${Math.min(100, ctx.getValue())}%` }} />
            </div>
            <span className="text-xs text-slate-500">{ctx.getValue()}%</span>
          </div>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: (ctx) => (
          <div className="inline-flex items-center gap-1">
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              onClick={() => setProgressTarget(ctx.row.original)}
              title="Update progress"
            >
              <Pencil className="h-4 w-4" />
            </button>
            <button
              className="rounded-md p-1.5 text-slate-500 hover:bg-indigo-50 hover:text-indigo-600"
              onClick={() => setHistoryTarget(ctx.row.original)}
              title="View evaluation history"
            >
              <History className="h-4 w-4" />
            </button>
          </div>
        ),
      }),
    ],
    [term.level],
  );

  return (
    <>
      <PageHeader
        title="QT Students Target"
        description="Assign Quran Tracker targets to students and track their progress."
        actions={
          <button className="btn-primary" onClick={() => setAssignOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> Assign Target
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-48" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
          <option value="">All {term.levelPlural}</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
        <Select className="!w-40" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="Assigned">Assigned</option>
          <option value="In Progress">In Progress</option>
          <option value="Completed">Completed</option>
          <option value="Reviewed">Reviewed</option>
        </Select>
      </div>

      <DataTable columns={columns} result={data} state={dt} loading={isFetching} emptyMessage="No assignments yet." />

      <AssignTargetModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSaved={invalidate}
        classes={classes}
        quranApi={quranApi}
        isOrg={isOrg}
      />

      {progressTarget && (
        <UpdateProgressModal target={progressTarget} onClose={() => setProgressTarget(null)} onSaved={invalidate} quranApi={quranApi} />
      )}

      {historyTarget && <ResponseHistoryModal target={historyTarget} onClose={() => setHistoryTarget(null)} quranApi={quranApi} />}
    </>
  );
}

function AssignTargetModal({
  open,
  onClose,
  onSaved,
  classes,
  quranApi,
  isOrg,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  classes: { id: string; name: string; sections?: { id: string; name: string }[] }[];
  quranApi: ReturnType<typeof createQuranTrackerApi>;
  isOrg: boolean;
}) {
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState('');
  const [studentId, setStudentId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [assignedTeacherId, setAssignedTeacherId] = useState('');
  const [assignedAyath, setAssignedAyath] = useState('');
  const [assignedDate, setAssignedDate] = useState(new Date().toISOString().slice(0, 10));
  const [targetDate, setTargetDate] = useState('');

  useEffect(() => {
    if (open) {
      setClassId('');
      setSectionId('');
      setStudentId('');
      setTargetId('');
      setAssignedTeacherId('');
      setAssignedAyath('');
      setAssignedDate(new Date().toISOString().slice(0, 10));
      setTargetDate('');
    }
  }, [open]);

  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: students = [] } = useQuery({
    queryKey: ['students-picker-for-quran-assign', isOrg, classId, sectionId],
    queryFn: () =>
      isOrg
        ? quranApi.studentsPicker({ classId: classId || undefined, sectionId: sectionId || undefined })
        : FeesApi.studentsPicker({ classId: classId || undefined, sectionId: sectionId || undefined }),
    enabled: open && !!classId,
  });

  const { data: teachers = [] } = useQuery({
    queryKey: ['teachers-picker-for-quran-assign'],
    queryFn: () => quranApi.teachersPicker(),
    enabled: open && isOrg,
  });

  const { data: targetsPage } = useQuery({
    queryKey: ['quran-targets-for-assign', isOrg, classId],
    queryFn: () => quranApi.listTargets({ classId: classId || undefined, limit: 100 }),
    enabled: open && !!classId,
  });
  const targets = targetsPage?.items ?? [];

  const save = useMutation({
    mutationFn: (payload: CreateQuranStudentTargetPayload) => quranApi.createStudentTarget(payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  const valid = !!studentId && !!targetId && (!isOrg || !!assignedTeacherId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assign Quran Tracker Target"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={save.isPending || !valid}
            onClick={() =>
              save.mutate({
                targetId,
                studentId,
                assignedTeacherId: isOrg ? assignedTeacherId : undefined,
                assignedAyath: assignedAyath || undefined,
                assignedDate: assignedDate || undefined,
                targetDate: targetDate || undefined,
              })
            }
          >
            {save.isPending ? 'Saving…' : 'Assign'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Class" required>
            <Select value={classId} onChange={(e) => { setClassId(e.target.value); setSectionId(''); setStudentId(''); setTargetId(''); }}>
              <option value="">— Select —</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Section">
            <Select value={sectionId} onChange={(e) => setSectionId(e.target.value)} disabled={!classId}>
              <option value="">All</option>
              {sections.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Student" required>
          <Select value={studentId} onChange={(e) => setStudentId(e.target.value)} disabled={!classId}>
            <option value="">— Select student —</option>
            {students.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.admissionNo})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Target" required>
          <Select value={targetId} onChange={(e) => setTargetId(e.target.value)} disabled={!classId}>
            <option value="">— Select target —</option>
            {targets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.surahName} ({t.ayathFrom}–{t.ayathTo})
              </option>
            ))}
          </Select>
        </Field>
        {isOrg && (
          <Field label="Assigned Teacher" required>
            <Select value={assignedTeacherId} onChange={(e) => setAssignedTeacherId(e.target.value)}>
              <option value="">— Select teacher —</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Assigned Ayath (optional note)">
          <Input value={assignedAyath} onChange={(e) => setAssignedAyath(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Assigned Date">
            <Input type="date" value={assignedDate} onChange={(e) => setAssignedDate(e.target.value)} />
          </Field>
          <Field label="Target Date">
            <Input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
          </Field>
        </div>
        {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>}
      </div>
    </Modal>
  );
}

function UpdateProgressModal({
  target,
  onClose,
  onSaved,
  quranApi,
}: {
  target: QuranStudentTargetRow;
  onClose: () => void;
  onSaved: () => void;
  quranApi: ReturnType<typeof createQuranTrackerApi>;
}) {
  const [completedAyath, setCompletedAyath] = useState(target.completedAyath ?? '');
  const [status, setStatus] = useState(target.status);
  const [completedPercent, setCompletedPercent] = useState(String(target.completedPercent));
  const [remarks, setRemarks] = useState(target.remarks ?? '');
  const [completedDate, setCompletedDate] = useState(target.completedDate ?? '');
  const [grade, setGrade] = useState(target.grade ?? '');

  const save = useMutation({
    mutationFn: (payload: UpdateQuranStudentTargetPayload) => quranApi.updateStudentTarget(target.id, payload),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Update Progress"
      description={`${target.studentName} — ${target.surahName} (${target.ayathFrom}–${target.ayathTo})`}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                completedAyath: completedAyath || undefined,
                status,
                completedPercent: Number(completedPercent) || 0,
                remarks: remarks || undefined,
                completedDate: completedDate || undefined,
                grade: grade || undefined,
              })
            }
          >
            {save.isPending ? 'Saving…' : 'Save Progress'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Status">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="Assigned">Assigned</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
            <option value="Reviewed">Reviewed</option>
          </Select>
        </Field>
        <Field label="Completed Ayath">
          <Input value={completedAyath} onChange={(e) => setCompletedAyath(e.target.value)} />
        </Field>
        <Field label="Completed %">
          <Input type="number" min="0" max="100" value={completedPercent} onChange={(e) => setCompletedPercent(e.target.value)} />
        </Field>
        <Field label="Completed Date">
          <Input type="date" value={completedDate} onChange={(e) => setCompletedDate(e.target.value)} />
        </Field>
        <Field label="Grade">
          <Input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="Optional" />
        </Field>
        <Field label="Remarks">
          <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        </Field>
        {save.error && <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errMsg(save.error)}</div>}
      </div>
    </Modal>
  );
}

function ResponseHistoryModal({
  target,
  onClose,
  quranApi,
}: {
  target: QuranStudentTargetRow;
  onClose: () => void;
  quranApi: ReturnType<typeof createQuranTrackerApi>;
}) {
  const { data, isFetching } = useQuery({
    queryKey: ['quran-tracker-responses', target.studentId, target.targetId],
    queryFn: () => quranApi.listTrackerResponses({ studentId: target.studentId, targetId: target.targetId, limit: 50 }),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Evaluation History"
      description={`${target.studentName} — ${target.surahName} (${target.ayathFrom}–${target.ayathTo})`}
      size="lg"
      footer={
        <button className="btn-secondary" onClick={onClose}>
          Close
        </button>
      }
    >
      {isFetching ? (
        <div className="py-6 text-center text-slate-400">Loading…</div>
      ) : !data?.items.length ? (
        <div className="py-6 text-center text-slate-400">No submitted responses yet.</div>
      ) : (
        <div className="space-y-3">
          {data.items.map((r) => (
            <div key={r.id} className="rounded-md border border-slate-200 p-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs text-slate-500">{dayjs(r.createdAt).format('DD MMM YYYY')}</span>
                <Badge tone={r.status === 'approved' ? 'green' : r.status === 'rejected' ? 'red' : 'amber'}>{r.status}</Badge>
              </div>
              <div className="text-sm text-slate-700">{r.ayathList}</div>
              {r.url && (
                <a href={r.url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs text-indigo-600 hover:text-indigo-800">
                  View submission
                </a>
              )}
              {(r.grade || r.evaluationRemarks) && (
                <div className="mt-2 rounded-md bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
                  {r.grade && <div>Grade: {r.grade}</div>}
                  {r.evaluationRemarks && <div>{r.evaluationRemarks}</div>}
                  {r.evaluationBy && <div className="text-slate-400">— {r.evaluationBy}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
